import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  IconButton,
  useDisclosure,
  useToast,
  Button,
  Collapse,
} from '@chakra-ui/react';
import { 
  AddIcon, 
  EditIcon, 
  QuestionIcon,
  DeleteIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import { 
  FaGripVertical 
} from 'react-icons/fa';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ICON_MAP } from '../../utils/iconMap';
import { useMenuTitle } from '../../hooks/useMenuTitle';
import MenuGroupModal from './MenuGroupModal';
import MenuItemModal from './MenuItemModal';

// Sortable item components
const SortableGroup = ({ group, onEdit, onAddItem, onDelete, isExpanded, onToggleExpand, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: group.id,
    data: {
      type: 'group',
      group,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      bg="white"
      p={4}
      borderRadius="md"
      boxShadow="sm"
      border="1px"
      borderColor="gray.200"
    >
      <VStack spacing={4} align="stretch">
        <HStack>
          <Icon
            {...attributes}
            {...listeners}
            as={FaGripVertical}
            color="gray.400"
            boxSize={5}
            cursor="grab"
          />
          <HStack flex="1">
            <IconButton
              icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              variant="ghost"
              size="sm"
              aria-label={isExpanded ? "Collapse Group" : "Expand Group"}
              onClick={() => onToggleExpand(group.id)}
            />
            {ICON_MAP[group.icon] && (
              <Icon as={ICON_MAP[group.icon]} boxSize={5} />
            )}
            <Text fontSize="lg" fontWeight="semibold">
              {group.name}
            </Text>
            <Box width="100px" /> {/* Spacer */}
            <IconButton
              icon={<AddIcon boxSize={3} />}
              size="sm"
              colorScheme="blue"
              aria-label="Add Menu Item"
              onClick={() => onAddItem(group)}
            />
          </HStack>
          <IconButton
            icon={<EditIcon />}
            variant="ghost"
            size="sm"
            aria-label="Edit Group"
            onClick={() => onEdit(group)}
          />
          <IconButton
            icon={<DeleteIcon />}
            variant="ghost"
            size="sm"
            colorScheme="red"
            aria-label="Delete Group"
            onClick={() => onDelete(group)}
          />
        </HStack>
        <Collapse in={isExpanded}>
          {children}
        </Collapse>
      </VStack>
    </Box>
  );
};

const SortableItem = ({ item, group, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: {
      type: 'item',
      item,
      groupId: group.id
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <HStack
      ref={setNodeRef}
      style={style}
      bg="gray.50"
      p={2}
      borderRadius="md"
    >
      <Icon
        {...attributes}
        {...listeners}
        as={FaGripVertical}
        color="gray.400"
        boxSize={4}
        cursor="grab"
      />
      {ICON_MAP[item.icon] && (
        <Icon as={ICON_MAP[item.icon]} boxSize={4} />
      )}
      <Text flex="1">{item.name}</Text>
      <IconButton
        icon={<EditIcon />}
        variant="ghost"
        size="sm"
        aria-label="Edit Menu Item"
        onClick={() => onEdit(group, item)}
      />
      <IconButton
        icon={<DeleteIcon />}
        variant="ghost"
        size="sm"
        colorScheme="red"
        aria-label="Delete Menu Item"
        onClick={() => onDelete(group, item)}
      />
    </HStack>
  );
};

export default function MenuManagement() {
  const menuTitle = useMenuTitle();
  const supabase = useSupabaseClient();
  const toast = useToast();
  const [menuGroups, setMenuGroups] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isAllExpanded, setIsAllExpanded] = useState(false); // Initialize to false since groups start collapsed
  const {
    isOpen: isGroupModalOpen,
    onOpen: onGroupModalOpen,
    onClose: onGroupModalClose,
  } = useDisclosure();
  const {
    isOpen: isItemModalOpen,
    onOpen: onItemModalOpen,
    onClose: onItemModalClose,
  } = useDisclosure();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch menu data
  const fetchMenuData = async () => {
    try {
      const { data: groups, error: groupsError } = await supabase
        .from('menu_groups')
        .select('*')
        .order('display_order');

      if (groupsError) throw groupsError;

      const { data: items, error: itemsError } = await supabase
        .from('menu_items')
        .select('*')
        .order('display_order');

      if (itemsError) throw itemsError;

      // Group menu items by menu_group_id
      const groupedItems = items.reduce((acc, item) => {
        if (!acc[item.menu_group_id]) {
          acc[item.menu_group_id] = [];
        }
        acc[item.menu_group_id].push(item);
        return acc;
      }, {});

      setMenuGroups(groups);
      setMenuItems(groupedItems);
    } catch (error) {
      console.error('Error fetching menu data:', error);
      toast({
        title: 'Error fetching menu data',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    fetchMenuData();
  }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    try {
      if (activeData.type === 'group') {
        // Get all groups
        const oldIndex = menuGroups.findIndex(g => g.id === active.id);
        const newIndex = menuGroups.findIndex(g => g.id === over.id);
        const updatedGroups = arrayMove(menuGroups, oldIndex, newIndex);

        // First update all groups to have high temporary orders
        const tempBase = 1000000;
        for (let i = 0; i < updatedGroups.length; i++) {
          const { id } = updatedGroups[i];
          await supabase
            .from('menu_groups')
            .update({ display_order: tempBase + i })
            .eq('id', id);
        }

        // Then update to final orders
        for (let i = 0; i < updatedGroups.length; i++) {
          const { id } = updatedGroups[i];
          await supabase
            .from('menu_groups')
            .update({ display_order: (i + 1) * 10 })
            .eq('id', id);
        }

        setMenuGroups(updatedGroups);
      } else if (activeData.type === 'item') {
        const activeGroupId = activeData.groupId;
        const overGroupId = overData.type === 'item' ? overData.groupId : overData.group.id;
        
        const activeItems = menuItems[activeGroupId] || [];
        const overItems = menuItems[overGroupId] || [];
        
        let updatedActiveItems = [...activeItems];
        let updatedOverItems = overGroupId === activeGroupId ? updatedActiveItems : [...overItems];
        
        // Handle moving within the same group
        if (activeGroupId === overGroupId) {
          const oldIndex = activeItems.findIndex(item => item.id === active.id);
          const newIndex = activeItems.findIndex(item => item.id === over.id);
          updatedActiveItems = arrayMove(updatedActiveItems, oldIndex, newIndex);
          
          // Update state immediately for responsive UI
          setMenuItems(prevItems => ({
            ...prevItems,
            [activeGroupId]: updatedActiveItems
          }));
          
          // First update to temporary orders
          const tempBase = 1000000;
          for (let i = 0; i < updatedActiveItems.length; i++) {
            const { id } = updatedActiveItems[i];
            await supabase
              .from('menu_items')
              .update({ 
                display_order: tempBase + i,
                menu_group_id: activeGroupId
              })
              .eq('id', id);
          }

          // Then update to final orders
          for (let i = 0; i < updatedActiveItems.length; i++) {
            const { id } = updatedActiveItems[i];
            await supabase
              .from('menu_items')
              .update({ 
                display_order: (i + 1) * 10,
                menu_group_id: activeGroupId
              })
              .eq('id', id);
          }
        } 
        // Handle moving between groups
        else {
          const oldIndex = activeItems.findIndex(item => item.id === active.id);
          const newIndex = overData.type === 'item' 
            ? overItems.findIndex(item => item.id === over.id)
            : 0;
          
          const [movedItem] = updatedActiveItems.splice(oldIndex, 1);
          updatedOverItems.splice(newIndex, 0, movedItem);
          
          // Update state immediately for responsive UI
          setMenuItems(prevItems => ({
            ...prevItems,
            [activeGroupId]: updatedActiveItems,
            [overGroupId]: updatedOverItems
          }));
          
          // First update all affected items to temporary orders
          const tempBase = 1000000;
          for (let i = 0; i < updatedActiveItems.length; i++) {
            const { id } = updatedActiveItems[i];
            await supabase
              .from('menu_items')
              .update({ 
                display_order: tempBase + i,
                menu_group_id: activeGroupId
              })
              .eq('id', id);
          }
          
          for (let i = 0; i < updatedOverItems.length; i++) {
            const { id } = updatedOverItems[i];
            await supabase
              .from('menu_items')
              .update({ 
                display_order: tempBase + updatedActiveItems.length + i,
                menu_group_id: overGroupId
              })
              .eq('id', id);
          }

          // Then update to final orders
          for (let i = 0; i < updatedActiveItems.length; i++) {
            const { id } = updatedActiveItems[i];
            await supabase
              .from('menu_items')
              .update({ 
                display_order: (i + 1) * 10,
                menu_group_id: activeGroupId
              })
              .eq('id', id);
          }
          
          for (let i = 0; i < updatedOverItems.length; i++) {
            const { id } = updatedOverItems[i];
            await supabase
              .from('menu_items')
              .update({ 
                display_order: (i + 1) * 10,
                menu_group_id: overGroupId
              })
              .eq('id', id);
          }
        }
        
        setMenuItems({
          ...menuItems,
          [activeGroupId]: updatedActiveItems,
          [overGroupId]: updatedOverItems
        });
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: 'Error updating order',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
      // Refresh data to ensure consistency
      fetchMenuData();
    }
  };

  const handleDeleteGroup = async (group) => {
    try {
      await supabase
        .from('menu_groups')
        .delete()
        .eq('id', group.id);

      await supabase
        .from('menu_items')
        .delete()
        .eq('menu_group_id', group.id);

      fetchMenuData();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: 'Error deleting group',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeleteItem = async (group, item) => {
    try {
      await supabase
        .from('menu_items')
        .delete()
        .eq('id', item.id);

      fetchMenuData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: 'Error deleting item',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(menuGroups.map(g => g.id)));
    }
    setIsAllExpanded(!isAllExpanded);
  };

  return (
    <Box h="100%">
      {/* Fixed Header */}
      <Box 
        position="fixed"
        top="0"
        right="0"
        left="320px" // Match the sidebar width from AuthenticatedLayout
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        zIndex="1"
        px={4}
        py={4}
      >
        <HStack justify="space-between" align="flex-start">
          <Box>
            <HStack>
              <Text fontSize="2xl" fontWeight="bold">
                {menuTitle}
              </Text>
              <Box width="100px" /> {/* Spacer */}
              <IconButton
                icon={<AddIcon boxSize={3} />}
                size="sm"
                colorScheme="blue"
                aria-label="Add Menu Group"
                onClick={() => {
                  setSelectedGroup(null);
                  onGroupModalOpen();
                }}
              />
            </HStack>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Note: Refresh the page to see menu order changes reflected in the sidebar
            </Text>
          </Box>
          <HStack spacing={4}>
            <Button
              size="sm"
              variant="outline"
              onClick={handleExpandAll}
            >
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
            <IconButton
              icon={<QuestionIcon />}
              variant="ghost"
              aria-label="Help"
              onClick={() => {
                // Add help modal or tooltip logic here
              }}
            />
          </HStack>
        </HStack>
      </Box>

      {/* Scrollable Content */}
      <Box 
        mt="120px" // Adjust based on header height
        px={4}
        overflowY="auto"
        h="calc(100vh - 120px)" // Adjust based on header height
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={menuGroups.map(g => g.id)}
            strategy={verticalListSortingStrategy}
          >
            <VStack spacing={4} align="stretch">
              {menuGroups.map((group) => (
                <SortableGroup
                  key={group.id}
                  group={group}
                  isExpanded={expandedGroups.has(group.id)}
                  onToggleExpand={(groupId) => {
                    setExpandedGroups(prev => {
                      const next = new Set(prev);
                      if (next.has(groupId)) {
                        next.delete(groupId);
                      } else {
                        next.add(groupId);
                      }
                      return next;
                    });
                  }}
                  onEdit={(group) => {
                    setSelectedGroup(group);
                    onGroupModalOpen();
                  }}
                  onAddItem={(group) => {
                    setSelectedGroup(group);
                    setSelectedItem(null);
                    onItemModalOpen();
                  }}
                  onDelete={(group) => handleDeleteGroup(group)}
                >
                  <SortableContext
                    items={(menuItems[group.id] || []).map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <VStack spacing={1} align="stretch" pl={8}>
                      {(menuItems[group.id] || []).map((item) => (
                        <SortableItem
                          key={item.id}
                          item={item}
                          group={group}
                          onEdit={(group, item) => {
                            setSelectedGroup(group);
                            setSelectedItem(item);
                            onItemModalOpen();
                          }}
                          onDelete={(group, item) => handleDeleteItem(group, item)}
                        />
                      ))}
                    </VStack>
                  </SortableContext>
                </SortableGroup>
              ))}
            </VStack>
          </SortableContext>
        </DndContext>
      </Box>

      <MenuGroupModal
        isOpen={isGroupModalOpen}
        onClose={onGroupModalClose}
        group={selectedGroup}
        onSave={fetchMenuData}
      />

      <MenuItemModal
        isOpen={isItemModalOpen}
        onClose={onItemModalClose}
        group={selectedGroup}
        item={selectedItem}
        onSave={fetchMenuData}
      />
    </Box>
  );
}
